import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router-dom'
import { CheckCircle2, AlertTriangle, User, Receipt } from 'lucide-react'
import { updateProfile } from '../../lib/api/auth'
import { isDemoEmail } from '../../lib/demo'
import { useAuth } from '../../store/authStore'
import { PageShell } from '../../components/layout/PageShell'
import { Field, Checkbox } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import type { PublicUser } from '../../lib/api/types'

type Form = {
  name: string
  lastname: string
  email: string
  phoneNumber: string
  licensePlate: string
  country: string
  businessName: string
  businessVatId: string
  businessSDI: string
  businessEmail: string
  businessAddress: string
  businessHouseNumber: string
  businessCity: string
  businessZipCode: string
  businessRegionProvince: string
  businessCountry: string
  isPublicAdministration: string
  commercialPrivacy: string
}

const blank: Form = {
  name: '', lastname: '', email: '', phoneNumber: '', licensePlate: '', country: 'IT',
  businessName: '', businessVatId: '', businessSDI: '', businessEmail: '', businessAddress: '',
  businessHouseNumber: '', businessCity: '', businessZipCode: '', businessRegionProvince: '',
  businessCountry: 'IT', isPublicAdministration: '', commercialPrivacy: '',
}

function fromUser(u: PublicUser): Form {
  return {
    name: u.name ?? '', lastname: u.lastname ?? '', email: u.email ?? '', phoneNumber: u.phoneNumber ?? '',
    licensePlate: (u.licensePlate ?? '').toUpperCase(), country: u.country || 'IT',
    businessName: u.businessName ?? '', businessVatId: u.businessVatId ?? '', businessSDI: u.businessSDI ?? '',
    businessEmail: u.businessEmail ?? '', businessAddress: u.businessAddress ?? '', businessHouseNumber: u.businessHouseNumber ?? '',
    businessCity: u.businessCity ?? '', businessZipCode: u.businessZipCode ?? '', businessRegionProvince: u.businessRegionProvince ?? '',
    businessCountry: u.businessCountry || 'IT',
    isPublicAdministration: u.isPublicAdministration === true || u.isPublicAdministration === 'true' ? 'true' : '',
    commercialPrivacy: u.commercialPrivacy === true || u.commercialPrivacy === 'true' ? 'true' : '',
  }
}
const hasBilling = (f: Form) =>
  Boolean(f.businessName || f.businessVatId || f.businessSDI || f.businessEmail || f.businessAddress || f.businessCity)

export function ProfilePage() {
  const { t } = useTranslation()
  const { locale: routeLocale = 'it' } = useParams()
  const status = useAuth((s) => s.status)
  const user = useAuth((s) => s.user)
  const setUser = useAuth((s) => s.setUser)

  const [form, setForm] = useState<Form>(blank)
  const [loaded, setLoaded] = useState(false)
  const [showBilling, setShowBilling] = useState(false)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<'saved' | 'error' | null>(null)

  useEffect(() => {
    if (user && !loaded) {
      const f = fromUser(user)
      setForm(f)
      setShowBilling(hasBilling(f))
      setLoaded(true)
    }
  }, [user, loaded])

  if (status === 'unknown')
    return (
      <PageShell narrow desktopWide>
        <div className="gsm-card h-64 animate-pulse" />
      </PageShell>
    )
  if (status === 'guest') return <Navigate to={`/${routeLocale}/login?redirect=/${routeLocale}/profile`} replace />

  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setMsg(null)
    setForm((p) => ({ ...p, [k]: e.target.value }))
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault()
    if (isDemoEmail(user?.email)) {
      // demo accounts are client-side only — never write to the backend
      setMsg('saved')
      return
    }
    // turning billing off wipes previously-saved invoicing data — confirm first
    if (!showBilling && user && hasBilling(fromUser(user)) && !window.confirm(t('profile.billingWipeConfirm'))) return
    setSaving(true)
    setMsg(null)
    try {
      // billing fields are only sent when the section is on (else cleared)
      const payload: Form = showBilling ? form : { ...form, ...billingCleared() }
      const updated = await updateProfile(payload)
      if (updated) {
        setUser(updated)
        setForm(fromUser(updated))
        setMsg('saved')
      } else {
        setMsg('error')
      }
    } catch {
      setMsg('error')
    } finally {
      setSaving(false)
    }
  }
  function billingCleared(): Partial<Form> {
    return {
      businessName: '', businessVatId: '', businessSDI: '', businessEmail: '', businessAddress: '',
      businessHouseNumber: '', businessCity: '', businessZipCode: '', businessRegionProvince: '',
      businessCountry: 'IT', isPublicAdministration: '',
    }
  }

  return (
    <PageShell narrow desktopWide>
      <div className="mb-6">
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-ink-900 sm:text-3xl">{t('profile.title')}</h1>
        <p className="mt-1.5 text-sm text-ink-500">{t('profile.subtitle')}</p>
      </div>

      <form onSubmit={onSave}>
       <fieldset disabled={saving} className="space-y-5">
        {/* desktop: personal + billing side by side; mobile/tablet: stacked (unchanged) */}
        <div className="grid gap-5 lg:grid-cols-2 lg:items-start">
        {/* personal */}
        <section className="gsm-card p-5 sm:p-6">
          <h2 className="mb-4 flex items-center gap-2 font-display text-sm font-bold text-ink-900">
            <User className="h-4 w-4 text-brand-600" /> {t('profile.personal')}
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={t('info.firstName')} value={form.name} onChange={set('name')} autoComplete="given-name" />
            <Field label={t('info.lastName')} value={form.lastname} onChange={set('lastname')} autoComplete="family-name" />
            <Field label={t('info.email')} type="email" value={form.email} onChange={set('email')} autoComplete="email" />
            <Field label={t('info.phone')} value={form.phoneNumber} onChange={set('phoneNumber')} autoComplete="tel" />
            <Field label={t('info.plate')} value={form.licensePlate} onChange={(e) => { setMsg(null); setForm((p) => ({ ...p, licensePlate: e.target.value.toUpperCase() })) }} />
            <Field label={t('info.country')} value={form.country} onChange={set('country')} autoComplete="country" />
          </div>
          <div className="mt-4 border-t border-ink-100 pt-3">
            <Checkbox
              checked={form.commercialPrivacy === 'true'}
              onChange={(v) => { setMsg(null); setForm((p) => ({ ...p, commercialPrivacy: v ? 'true' : '' })) }}
              label={t('info.marketing')}
            />
          </div>
        </section>

        {/* billing */}
        <section className="gsm-card p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <h2 className="flex items-center gap-2 font-display text-sm font-bold text-ink-900">
              <Receipt className="h-4 w-4 text-brand-600" /> {t('profile.billing')}
            </h2>
          </div>
          <p className="mt-1 text-xs text-ink-400">{t('profile.billingHint')}</p>
          <div className="mt-3">
            <Checkbox checked={showBilling} onChange={(v) => { setMsg(null); setShowBilling(v) }} label={t('profile.addBilling')} />
          </div>

          {showBilling && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-ink-100 pt-4 sm:grid-cols-2">
              <Field className="sm:col-span-2" label={t('payment.businessName')} value={form.businessName} onChange={set('businessName')} autoComplete="organization" />
              <Field label={t('payment.vat')} value={form.businessVatId} onChange={set('businessVatId')} />
              <Field label={t('payment.sdi')} value={form.businessSDI} onChange={set('businessSDI')} />
              <Field className="sm:col-span-2" label={t('payment.businessEmail')} type="email" value={form.businessEmail} onChange={set('businessEmail')} autoComplete="email" />
              <Field label={t('payment.address')} value={form.businessAddress} onChange={set('businessAddress')} autoComplete="address-line1" />
              <Field label={t('payment.houseNumber')} value={form.businessHouseNumber} onChange={set('businessHouseNumber')} />
              <Field label={t('payment.city')} value={form.businessCity} onChange={set('businessCity')} autoComplete="address-level2" />
              <Field label={t('payment.zip')} value={form.businessZipCode} onChange={set('businessZipCode')} autoComplete="postal-code" />
              <Field label={t('payment.province')} value={form.businessRegionProvince} onChange={set('businessRegionProvince')} autoComplete="address-level1" />
              <Field label={t('payment.businessCountry')} value={form.businessCountry} onChange={set('businessCountry')} autoComplete="country" />
              <div className="sm:col-span-2">
                <Checkbox
                  checked={form.isPublicAdministration === 'true'}
                  onChange={(v) => setForm((p) => ({ ...p, isPublicAdministration: v ? 'true' : '' }))}
                  label={t('payment.pa')}
                />
              </div>
            </div>
          )}
        </section>
        </div>

        {msg === 'saved' && (
          <p className="flex items-center gap-2 rounded-xl bg-green-50 px-4 py-2.5 text-sm font-medium text-green-700">
            <CheckCircle2 className="h-4 w-4" /> {t('profile.saved')}
          </p>
        )}
        {msg === 'error' && (
          <p className="flex items-center gap-2 rounded-xl bg-brand-50 px-4 py-2.5 text-sm font-medium text-brand-700">
            <AlertTriangle className="h-4 w-4" /> {t('profile.saveError')}
          </p>
        )}

        <div className="lg:flex lg:justify-end">
          <Button type="submit" size="lg" className="w-full lg:w-auto lg:px-12" loading={saving}>
            {t('profile.save')}
          </Button>
        </div>
       </fieldset>
      </form>
    </PageShell>
  )
}
